import { useEffect, useMemo, useState } from "react";
import {
  Accordion,
  Badge,
  Button,
  Card,
  Group,
  Modal,
  NumberInput,
  Select,
  SimpleGrid,
  Skeleton,
  Stack,
  Switch,
  Table,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { languageName } from "@mip-tg-bot-turntable/platform-api";
import { adminErrText, adminGet, adminSend, AdminApiError } from "../../api.ts";
import { AdminTable } from "../../components/AdminTable.tsx";
import { TablePagination } from "../../components/TablePagination.tsx";
import { useClientPagination } from "../../hooks/useClientPagination.ts";

// 语言下拉选项：该 bot 启用语言 + default（兜底桶）。
const langSelectData = (languages: readonly string[]) => [
  ...languages.map((c) => ({ value: c, label: languageName(c) })),
  { value: "default", label: "默认 / 兜底 (default)" },
];

interface EditCommandForm {
  label: string;
  replyTemplate: string;
  displayOrder: number;
}

interface CommandRow {
  id: string;
  command: string;
  language_code: string;
  label: string;
  reply_template: string | null;
  is_enabled: boolean;
  display_order: number;
}

interface CreateForm {
  command: string;
  languageCode: string;
  label: string;
  replyTemplate: string;
  displayOrder: number;
}

export function CommandsTab(props: {
  readonly botId: string;
  readonly languages: readonly string[];
}) {
  const { botId } = props;
  const qc = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [editingRow, setEditingRow] = useState<CommandRow | null>(null);
  const [langFilter, setLangFilter] = useState<string>("all");
  const [enabledFilter, setEnabledFilter] = useState<"all" | "on" | "off">("all");
  const [search, setSearch] = useState("");

  const editForm = useForm<EditCommandForm>({
    initialValues: { label: "", replyTemplate: "", displayOrder: 0 },
    validate: {
      label: (v) => (v.trim().length === 0 ? "必填" : null),
    },
  });

  const openEdit = (row: CommandRow) => {
    setEditingRow(row);
    editForm.setValues({
      label: row.label,
      replyTemplate: row.reply_template ?? "",
      displayOrder: row.display_order,
    });
  };

  const form = useForm<CreateForm>({
    initialValues: {
      command: "",
      languageCode: "en-IN",
      label: "",
      replyTemplate: "",
      displayOrder: 0,
    },
    validate: {
      command: (v) =>
        v.trim().length === 0
          ? "必填"
          : /^[a-z0-9_]+$/.test(v)
            ? null
            : "只允许小写字母/数字/下划线",
      label: (v) => (v.trim().length === 0 ? "必填" : null),
    },
  });

  const list = useQuery({
    queryKey: ["bot", botId, "commands"],
    queryFn: () => adminGet<{ items: CommandRow[] }>(`/bots/${botId}/commands`),
  });
  const create = useMutation({
    mutationFn: (v: CreateForm) => adminSend("POST", `/bots/${botId}/commands`, v),
    onSuccess: () => {
      notifications.show({ color: "green", message: "已添加" });
      setCreateOpen(false);
      form.reset();
      void qc.invalidateQueries({ queryKey: ["bot", botId, "commands"] });
    },
    onError: (e) =>
      notifications.show({ color: "red", message: adminErrText((e as AdminApiError).code) }),
  });
  const patch = useMutation({
    mutationFn: (args: { id: string; payload: unknown }) =>
      adminSend("PATCH", `/bots/${botId}/commands/${args.id}`, args.payload),
    onSuccess: () => {
      notifications.show({ color: "green", message: "已更新" });
      setEditingRow(null);
      void qc.invalidateQueries({ queryKey: ["bot", botId, "commands"] });
    },
    onError: (e) =>
      notifications.show({ color: "red", message: adminErrText((e as AdminApiError).code) }),
  });
  const remove = useMutation({
    mutationFn: (id: string) => adminSend("DELETE", `/bots/${botId}/commands/${id}`),
    onSuccess: () => {
      notifications.show({ color: "green", message: "已删除" });
      void qc.invalidateQueries({ queryKey: ["bot", botId, "commands"] });
    },
    onError: (e) =>
      notifications.show({ color: "red", message: adminErrText((e as AdminApiError).code) }),
  });

  // 客服 / 链接配置（bot.links）。support_handle 供 /support 命令文案占位。
  const botQuery = useQuery({
    queryKey: ["bot", botId, "detail"],
    queryFn: () => adminGet<{ links?: Record<string, string> | null }>(`/bots/${botId}`),
  });
  const [supportHandle, setSupportHandle] = useState("");
  const [linksFilled, setLinksFilled] = useState(false);
  useEffect(() => {
    if (botQuery.data && !linksFilled) {
      setLinksFilled(true);
      setSupportHandle(botQuery.data.links?.["support_handle"] ?? "");
    }
  }, [botQuery.data, linksFilled]);
  const saveLinks = useMutation({
    mutationFn: () => {
      const next: Record<string, string> = { ...botQuery.data?.links };
      const v = supportHandle.trim();
      if (v === "") delete next["support_handle"];
      else next["support_handle"] = v;
      return adminSend("PATCH", `/bots/${botId}`, { links: next });
    },
    onSuccess: () => {
      notifications.show({ color: "green", message: "已保存" });
      void qc.invalidateQueries({ queryKey: ["bot", botId, "detail"] });
    },
    onError: (e) =>
      notifications.show({ color: "red", message: adminErrText((e as AdminApiError).code) }),
  });

  // group by command name；按语言/启用/关键字筛后再分页
  const allItems = list.data?.items ?? [];
  const keyword = search.trim().toLowerCase();
  const groupedEntries = useMemo(() => {
    const grouped = new Map<string, CommandRow[]>();
    for (const row of allItems) {
      if (langFilter !== "all" && row.language_code !== langFilter) continue;
      if (enabledFilter === "on" && !row.is_enabled) continue;
      if (enabledFilter === "off" && row.is_enabled) continue;
      if (keyword !== "") {
        const cmdHit = row.command.toLowerCase().includes(keyword);
        const labelHit = row.label.toLowerCase().includes(keyword);
        if (!cmdHit && !labelHit) continue;
      }
      const existing = grouped.get(row.command);
      if (existing !== undefined) {
        existing.push(row);
      } else {
        grouped.set(row.command, [row]);
      }
    }
    return Array.from(grouped.entries());
  }, [allItems, langFilter, enabledFilter, keyword]);
  const resetKey = `${langFilter}|${enabledFilter}|${keyword}`;
  const {
    pageItems: pagedGroups,
    page,
    setPage,
    pageSize,
    total,
    totalPages,
  } = useClientPagination(groupedEntries, resetKey);

  return (
    <>
      <Card shadow="sm" padding="lg" radius="md" withBorder mb="md">
        <Text fw={600} size="lg" mb="md">
          客服 / 链接配置
        </Text>
        <Stack gap="sm">
          <TextInput
            label="客服账号（support_handle）"
            description="供 /support 命令文案的 {{support_handle}} 占位，如 @yoursupport"
            placeholder="@support"
            value={supportHandle}
            onChange={(e) => setSupportHandle(e.currentTarget.value)}
          />
          <Button w="fit-content" loading={saveLinks.isPending} onClick={() => saveLinks.mutate()}>
            保存
          </Button>
        </Stack>
      </Card>

      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Group justify="space-between" mb="md">
          <Text fw={600} size="lg">
            命令
          </Text>
          <Button onClick={() => setCreateOpen(true)}>新建</Button>
        </Group>
        <Group gap="sm" mb="md" wrap="wrap">
          <Select
            size="xs"
            label="语言"
            data={[{ value: "all", label: "全部" }, ...langSelectData(props.languages)]}
            value={langFilter}
            onChange={(v) => setLangFilter(v ?? "all")}
            style={{ width: 180 }}
            clearable={false}
            searchable
          />
          <Select
            size="xs"
            label="启用"
            data={[
              { label: "全部", value: "all" },
              { label: "启用", value: "on" },
              { label: "禁用", value: "off" },
            ]}
            value={enabledFilter}
            onChange={(v) => setEnabledFilter((v ?? "all") as "all" | "on" | "off")}
            style={{ width: 120 }}
            clearable={false}
          />
          <TextInput
            size="xs"
            label="搜索"
            placeholder="command / label"
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            style={{ minWidth: 200, flex: 1 }}
          />
        </Group>
        {list.isLoading ? (
          <Stack gap="xs">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} h={48} radius="sm" />
            ))}
          </Stack>
        ) : allItems.length === 0 ? (
          <Text c="dimmed" ta="center" py="xl">
            暂无命令配置
          </Text>
        ) : groupedEntries.length === 0 ? (
          <Text c="dimmed" ta="center" py="xl">
            无匹配命令
          </Text>
        ) : (
          <>
            <Accordion variant="contained">
              {pagedGroups.map(([cmd, rows]) => (
                <Accordion.Item key={cmd} value={cmd}>
                  <Accordion.Control>
                    <Group gap="xs">
                      <Badge color="blue" variant="filled" size="sm">
                        /{cmd}
                      </Badge>
                      <Text size="sm" c="dimmed">
                        {rows.length} 种语言
                      </Text>
                    </Group>
                  </Accordion.Control>
                  <Accordion.Panel>
                    <AdminTable minWidth={500}>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th>语言</Table.Th>
                          <Table.Th>显示标签</Table.Th>
                          <Table.Th>回复模板</Table.Th>
                          <Table.Th>顺序</Table.Th>
                          <Table.Th>启用</Table.Th>
                          <Table.Th>操作</Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {rows.map((row) => (
                          <Table.Tr key={row.id}>
                            <Table.Td>{row.language_code}</Table.Td>
                            <Table.Td>{row.label}</Table.Td>
                            <Table.Td
                              style={{
                                maxWidth: 200,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {row.reply_template ?? "-"}
                            </Table.Td>
                            <Table.Td>{row.display_order}</Table.Td>
                            <Table.Td>
                              <Switch
                                checked={row.is_enabled}
                                onChange={(e) =>
                                  patch.mutate({
                                    id: row.id,
                                    payload: { isEnabled: e.currentTarget.checked },
                                  })
                                }
                              />
                            </Table.Td>
                            <Table.Td>
                              <Group gap={4}>
                                <Button size="xs" variant="outline" onClick={() => openEdit(row)}>
                                  编辑
                                </Button>
                                <Button
                                  size="xs"
                                  color="red"
                                  variant="outline"
                                  onClick={() => {
                                    modals.openConfirmModal({
                                      title: "确认删除",
                                      children: `确认删除命令「/${row.command}」（${row.language_code}）？`,
                                      labels: { confirm: "删除", cancel: "取消" },
                                      confirmProps: { color: "red" },
                                      onConfirm: () => remove.mutate(row.id),
                                    });
                                  }}
                                >
                                  删除
                                </Button>
                              </Group>
                            </Table.Td>
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                    </AdminTable>
                  </Accordion.Panel>
                </Accordion.Item>
              ))}
            </Accordion>
            <TablePagination
              page={page}
              pageSize={pageSize}
              total={total}
              totalPages={totalPages}
              onPageChange={setPage}
            />
          </>
        )}
      </Card>

      <Modal
        title={
          editingRow !== null
            ? `编辑命令：/${editingRow.command} (${editingRow.language_code})`
            : ""
        }
        opened={editingRow !== null}
        onClose={() => setEditingRow(null)}
        keepMounted={false}
        size="xl"
      >
        <form
          onSubmit={editForm.onSubmit((v) =>
            patch.mutate({
              id: editingRow?.id ?? "",
              payload: {
                label: v.label,
                replyTemplate: v.replyTemplate || null,
                displayOrder: v.displayOrder,
              },
            }),
          )}
        >
          <Stack gap="sm">
            <SimpleGrid cols={2}>
              <TextInput label="显示标签" required {...editForm.getInputProps("label")} />
              <NumberInput label="顺序" {...editForm.getInputProps("displayOrder")} />
            </SimpleGrid>
            <Textarea label="回复模板" rows={2} {...editForm.getInputProps("replyTemplate")} />
            <Button type="submit" loading={patch.isPending}>
              保存
            </Button>
          </Stack>
        </form>
      </Modal>

      <Modal
        title="新建命令"
        opened={createOpen}
        onClose={() => setCreateOpen(false)}
        keepMounted={false}
        size="xl"
      >
        <form onSubmit={form.onSubmit((v) => create.mutate(v))}>
          <Stack gap="sm">
            <SimpleGrid cols={2}>
              <TextInput
                label="命令（小写 / 数字 / 下划线）"
                placeholder="start"
                required
                {...form.getInputProps("command")}
              />
              <Select
                label="语言"
                data={langSelectData(props.languages)}
                searchable
                {...form.getInputProps("languageCode")}
              />
              <TextInput label="显示标签" required {...form.getInputProps("label")} />
              <NumberInput label="顺序" {...form.getInputProps("displayOrder")} />
            </SimpleGrid>
            <Textarea label="回复模板" rows={2} {...form.getInputProps("replyTemplate")} />
            <Button type="submit" loading={create.isPending}>
              保存并同步面板
            </Button>
          </Stack>
        </form>
      </Modal>
    </>
  );
}
